import React, { useCallback, useState, useEffect } from 'react';
import './form.css';
import { fileUpload, formUpload, updateForm, getAttachments } from '../../api';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import LoadingSpinner from '../loadingSpinner';
import { Guidance } from '../guidance';
import { useWindowSize } from '../../hooks/useWindowSize';
import { FaFileDownload } from "react-icons/fa";
import { toast } from 'react-toastify';
import {
    MAX_ATTACHMENT_FILE_SIZE,
    MOBILE_WIDTH,
    GUIDANCE_MIN_WIDTH,
    GUIDANCE_MAX_WIDTH,
    SUBMISSION_STATUS,
    ACCEPTED_FILE_TYPES,
    FORM_TYPES,
    TOAST_TYPES,
    FORM_META,
    CLASSIFICATION
} from './constants';
import { Workflow } from './workflow';
import { Checkbox } from '../checkbox';
import { RadioButton } from '../radioButton';
import { Input } from '../input';
import { ActionButton } from './actionButton';
import { DownloadPdfButton } from './downloadPdfButton';

// imports and plugins for file uploader
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
registerPlugin(FilePondPluginImagePreview); // displays a preview of image files
registerPlugin(FilePondPluginFileValidateSize); // allows for file size filtering
registerPlugin(FilePondPluginFileValidateType); // allows for limiting which file types are acceptable

const handleToast = (type, message) => {
    switch(type) {
        case TOAST_TYPES.SUCCESS:
            toast.success(message);
            break;
        case TOAST_TYPES.WARNING:
            toast.warning(message);
            break;
        case TOAST_TYPES.ERROR:
            toast.error(message);
            break;
        case TOAST_TYPES.INFO:
            toast.info(message);
            break;
        default:
            break;
    }
}

/**
 * @param {bool} isInternal depicts whether the form is for contractors(false) or pnnl(true)
 * @param {object} prefillFormData form object from server - (internal only)
 * @param {number} formId form's unique identifier
 * @param {function} onSubmitted close form and open submission complete page
 */

// 2 use cases
// Submitting the document, with readonly access for the response and other PNNL-only fields
// Responding to document, with write-access to PNNL-only fields, but nothing else

// if isInternal flag is TRUE, this is a request for info and PNNL-only fields will be disabled
export default function RfiForm ({ isInternal, prefillFormData, formId, onSubmitted }) {
    const [formData, setFormData] = useState(initializeForm(prefillFormData));
    const [formErrors, setFormErrors] = useState({});
    const [files, setFiles] = useState([]); // files the user tries to submit with the form (external only)
    const [fileError, setFileError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isExternalFormSubmitted, setIsExternalFormSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isComplete, setIsComplete] = useState(formData.status ? (formData.status === SUBMISSION_STATUS.COMPLETE ? true : false) : false);
    const [isSameAsSubmitter, setIsSameAsSubmitter] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [loadingAttachments, setLoadingAttachments] = useState(false);

    // used for recalculating page dimensions for mobile devices;
    // informative guidance component needs to recalculate width for smaller devices
    const { width } = useWindowSize();
    const isMobileDimensions = width < MOBILE_WIDTH;
    const [guidanceWidth, setGuidanceWidth] = useState(GUIDANCE_MAX_WIDTH);
    const [guidanceDisplayType, setGuidanceDisplayType] = useState('tooltip');
    const [guidanceAlignment, setGuidanceAlignment] = useState(null); //refers to which side guidance component the text box should render ('left' or 'right')
    const isEditable = formData?.status !== SUBMISSION_STATUS.COMPLETE;

    // adjusts styling for guidance components when resolution drops below 600
    useEffect(() => {
        if (isMobileDimensions && guidanceWidth === GUIDANCE_MAX_WIDTH) {
            setGuidanceWidth(GUIDANCE_MIN_WIDTH);
            setGuidanceDisplayType('dialog'); // 'dialog' offers onHover activation for computers
            setGuidanceAlignment('right');
        }
        else if (!isMobileDimensions && guidanceWidth === GUIDANCE_MIN_WIDTH) {
            setGuidanceWidth(GUIDANCE_MAX_WIDTH);
            setGuidanceDisplayType('tooltip'); // 'tooltip' offers onClick activation for mobile devices
            setGuidanceAlignment(null);
        }
    }, [width, guidanceWidth, isMobileDimensions]);

    // generic function for setting form object fields
    function setField(key, value) {
        setFormData(fields => ({
            ...fields,
            [key]: value
        }));
    }
    
    const fetchAttachments = useCallback(() => {
        setLoadingAttachments(true);

        getAttachments(formId)
        .then(response => {
            setAttachments(response);
        })
        .catch(error => {
            handleToast(TOAST_TYPES.Error, <div className="toast-message">A problem occured while fetching attachments. <span>Please try refreshing the page.</span></div>);
        })
        .finally(() => setLoadingAttachments(false));
    }, [formId, setLoadingAttachments]);
    
    useEffect(() => {
        fetchAttachments();
    }, [fetchAttachments]);
    
    // generic function for setting error messages
    function setErrorField(key, errorMessage) {
        setFormErrors(errors => ({
            ...errors,
            [key]: errorMessage
        }));
    }

    // set files stored in filePond component
    const handleFileUpload = (filesToUpload) => {
        setFiles(filesToUpload);
    }

    // returns false if there are any uploaded files with invalid file types or exceeds the max file size
    const hasInvalidFiles = useCallback(() => {
        return files.some(file => {
            const badFileType = !Object.keys(ACCEPTED_FILE_TYPES).includes(file.fileType);
            const fileTooBig = file.fileSize > MAX_ATTACHMENT_FILE_SIZE;
            if (badFileType) {
                setFileError('1 or more files are not valid file types.');
            }
            else if (fileTooBig) {
                setFileError('1 or more files exceeds the max file size.');
            }
            return (badFileType || fileTooBig);
        });
    }, [files, setFileError]);

    // submits the form and attachments
    const submitRFI = (e, isSaving) => {
        e.preventDefault();

        // reset form errors
        setFormErrors({});
        setFileError(null);

        // validate email and phone number fields
        const phoneRegex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/;
        const noSpacePhoneRegex = /\d{10}/; // matches 10 consecutive digits
        const emailRegex = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
        const phoneMatch = formData.submittersPhone.match(phoneRegex);
        const noSpaceMatch = formData.submittersPhone.match(noSpacePhoneRegex);
        const emailMatch = formData.submittersEmail.match(emailRegex);

        let isValid = true;
        if(phoneMatch === null && noSpaceMatch === null) {
            setErrorField('submittersPhone', 'Invalid phone number.');
            isValid = false;
        } 
        if (emailMatch === null) {
            setErrorField('submittersEmail', 'Invalid email format.');
            isValid = false;
        }
        
        // ensure required fields are filled
        if (!isRequiredFieldsComplete(formData, isInternal, setErrorField, isSaving)) {
            isValid = false;
        }
        
        // check if there are any invalid files trying to be sent
        if (hasInvalidFiles()) {
            isValid = false;
        }

        // escape if any of the validation failed
        if (!isValid) { return; }

        // generate a unique id for this form and its attachments
        const uniqueIdentifier = formId || Date.now();

        // rfi's submitted by external site are tagged as 'Submitted'
        // rfi's submitted by internal site are tagged as 'Complete'
        let newStatus = formData.status;
        if (isInternal) {
            if (newStatus === SUBMISSION_STATUS.SUBMITTED || newStatus === SUBMISSION_STATUS.IN_PROGRESS) {
                if (isSaving) {
                    newStatus = SUBMISSION_STATUS.IN_PROGRESS;
                }
                else {
                    newStatus = SUBMISSION_STATUS.DC_REVIEW;
                }
            }
        }
        else {
            newStatus = SUBMISSION_STATUS.SUBMITTED;
        }

        const finishedForm = {
            ...formData,
            status: newStatus
        }

        // ignore these fields if their boxes aren't checked
        if (!finishedForm.scheduleImpact) {
            finishedForm.scheduleDescription = '';
            setField('scheduleDescription', '');
        }
        if (!finishedForm.costImpact) {
            finishedForm.costDescription = '';
            setField('costDescription', '');
        }
        isSaving ? setSaving(true) : setLoading(true);

        // generate api calls for each of the files
        const fileApiCalls = files.map(file => {
            return fileUpload(uniqueIdentifier, file);
        });

        // begin sending the files
        Promise.all(fileApiCalls)
        .then(allResponses => {
            let success = true;
            allResponses.forEach(response => {
                // TODO: Message could be dynamic based on the error range of the api returned.
                if (response.status !== 200) {
                    success = false;
                    handleToast(TOAST_TYPES.Error, <div className="toast-message">A problem occured uploading file. <span>Please try submitting the form again.</span></div>);
                }
            });
            // submitting the form
            if (success) {
                if (isSaving) {
                    saveForm(finishedForm, uniqueIdentifier);
                }
                else if (isInternal) {
                    submitInternalForm(finishedForm, uniqueIdentifier);
                }
                else {
                    submitExternalForm(finishedForm, uniqueIdentifier);
                }
            }
            else {
                isSaving ? setSaving(false) : setLoading(false);
            }
        });
    }

    // handles submitting a document, from the external site
    function submitExternalForm (formData, uniqueIdentifier) {
        formUpload(uniqueIdentifier, formData)
            .then(response => {
                setIsExternalFormSubmitted(true);
                onSubmitted({
                    ...formData,
                    rfiNumber: uniqueIdentifier
                },
               files);
            })
            .catch(e => {
                setIsExternalFormSubmitted(false);
                handleToast(TOAST_TYPES.ERROR, <div className="toast-message">A problem occured submitting the form. <span>Please try submitting the form again.</span></div>);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    // handles saving from internal site
    function submitInternalForm (formData, uniqueIdentifier) {
        updateForm(uniqueIdentifier, formData)
            .then(response => {
                handleToast(TOAST_TYPES.SUCCESS, <div className="toast-message">Response complete! Awaiting DC Review.</div>);
                setFiles([]);
                fetchAttachments();
                setField('status', SUBMISSION_STATUS.DC_REVIEW);
            })
            .catch(e => {
                handleToast(TOAST_TYPES.ERROR, <div className="toast-message">A problem occured submitting the form. <span>Please try submitting the form again.</span></div>);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    // handles saving from internal site
    function saveForm (formData, uniqueIdentifier) {
        updateForm(uniqueIdentifier, formData)
            .then(response => {
                handleToast(TOAST_TYPES.SUCCESS, "Form saved!");
                setFiles([]);
                fetchAttachments();
                setField('status', response.status);
            })
            .catch(e => {
                handleToast(TOAST_TYPES.ERROR, <div className="toast-message">An unexpected error occured. <span>Try saving again.</span></div>);
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function submitDcReview (e) {
        e.preventDefault();
        const finishedForm = {
            ...formData,
            status: SUBMISSION_STATUS.PENDING_DISTRIBUTION
        }

        setLoading(true);

        updateForm(formId, finishedForm)
            .then(response => {
                handleToast(TOAST_TYPES.SUCCESS, <div className="toast-message">DC Review Complete!</div>);
                setField('status', SUBMISSION_STATUS.PENDING_DISTRIBUTION);
            })
            .catch(error => {
                handleToast(TOAST_TYPES.ERROR, <div className="toast-message">A problem occured submitting the form. <span>Please try submitting the form again.</span></div>);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function submitCompletion (e) {
        e.preventDefault();
        const finishedForm = {
            ...formData,
            status: SUBMISSION_STATUS.COMPLETE
        }

        setLoading(true);

        updateForm(formId, finishedForm)
        .then(response => {
            setIsComplete(true);
            handleToast(TOAST_TYPES.SUCCESS, <div className="toast-message">RFI process complete!</div>);
            setField('status', SUBMISSION_STATUS.COMPLETE);
        })
        .catch(error => {
            handleToast(TOAST_TYPES.ERROR, <div className="toast-message">A problem occured submitting the form. <span>Please try submitting the form again.</span></div>);
            // TODO: handle error
        })
        .finally(() => {
            setLoading(false);
        });
    }

    function handleSubmit(e, isSaving = false) {
        if (isSaving) {
            submitRFI(e, true);
        }
        else {
            switch (formData.status) {
                case SUBMISSION_STATUS.SUBMITTED:
                case SUBMISSION_STATUS.IN_PROGRESS:
                    submitRFI(e, isSaving);
                    break;
                case SUBMISSION_STATUS.DC_REVIEW:
                    submitDcReview(e, isSaving);
                    break;
                case SUBMISSION_STATUS.PENDING_DISTRIBUTION:
                    submitCompletion(e, isSaving);
                    break;
                default:
                    submitRFI(e, isSaving);
            }
        }
    }

    // Renders an 'i' icon with text describing the adjacent field 
    const HelperText = useCallback(({ content, alignment }) => {
        return (
            <Guidance
                displayMode={guidanceDisplayType}
                type="info"
                content={content}
                bgIconSize="sm"
                from={alignment ? alignment : 'left'}
                width={guidanceDisplayType !== 'tooltip' ? `${guidanceWidth}px` : 'max-content'}
                noTab={true}
            />);
    }, [guidanceDisplayType, guidanceWidth]);

    const sameAsSubmitterOnClick = () => {
        if (isSameAsSubmitter === false) {
            // copy the submitters name into the requested by field
            const submittersName = formData.submittersName;
            setField('requestedBy', submittersName);
        }
        setIsSameAsSubmitter(state => !state);
    }

    return (
        <div className="container">
            {isInternal &&
                <Workflow status={formData?.status} />
            }
            <div className="form">
                {/* Preliminary information section */}
                <div className="border">
                    <Input
                        label="Contract Number:"
                        value={formData.contractNumber}
                        onChange={(newValue) => setField('contractNumber', newValue)}
                        isReadOnly={isComplete}
                        helperTextComponent={<HelperText content={"PNNL contract number (if available)."} />}
                        errorMessage={formErrors['contractNumber']}
                    />
                    <Input
                        label="Task Order Number:"
                        value={formData.taskOrderNumber}
                        onChange={(newValue) => setField('taskOrderNumber', newValue)}
                        isReadOnly={isComplete}
                        errorMessage={formErrors['taskOrderNumber']}
                    />
                    <Input
                        label="Project Name:"
                        value={formData.projectName}
                        onChange={(newValue) => setField('projectName', newValue)}
                        isReadOnly={isComplete}
                        isRequired
                        errorMessage={formErrors['projectName']}
                    />
                    <Input
                        label="Project Location:"
                        value={formData.projectLocation}
                        onChange={(newValue) => setField('projectLocation', newValue)}
                        isReadOnly={isComplete}
                        helperTextComponent={<HelperText content={"Port of Entry and project description (RO, reconfiguration, upgrade, etc.)"} />}
                        isRequired
                        errorMessage={formErrors['projectLocation']}
                    />
                    <Input
                        label="Contractor:"
                        value={formData.contractor}
                        onChange={(newValue) => setField('contractor', newValue)}
                        isReadOnly={isComplete}
                        helperTextComponent={<HelperText content={"Company name"} />}
                        isRequired
                        errorMessage={formErrors['contractor']}
                    />
                    {isInternal &&
                        <Input
                            label="RFI Number:"
                            value={formData.rfiNumber}
                            onChange={(newValue) => setField('rfiNumber', newValue)}
                            isReadOnly={isComplete}
                            errorMessage={formErrors['rfiNumber']}
                        />
                    }
                    <Input
                        label="Date:"
                        isReadOnly={isComplete}
                        helperTextComponent={<HelperText content={"Today's date"}/>}
                        isRequired
                        errorMessage={formErrors['submissionDate']}
                        inputComponent={
                            <DatePicker
                                selected={formData.submissionDate}
                                onChange={(e) => setField('submissionDate', e)}
                                className="date-picker"
                                readOnly={isComplete}
                            />
                        }
                    />
                    <Input
                        label="Subject:"
                        value={formData.subject}
                        onChange={(newValue) => setField('subject', newValue)}
                        isReadOnly={isComplete}
                        helperTextComponent={<HelperText content={"Issue that needs to be resolved."}/>}
                        isRequired
                        errorMessage={formErrors['subject']}
                    />
                    <Input
                        label="Submitter's Name:"
                        value={formData.submittersName}
                        onChange={(newValue) => setField('submittersName', newValue)}
                        isReadOnly={isComplete}
                        isRequired
                        errorMessage={formErrors['submittersName']}
                    />
                    <Input
                        label="Submitter's Email:"
                        value={formData.submittersEmail}
                        onChange={(newValue) => setField('submittersEmail', newValue)}
                        isReadOnly={isComplete}
                        isRequired
                        errorMessage={formErrors['submittersEmail']}
                    />
                    <Input
                        label="Submitter's Phone Number:"
                        value={formData.submittersPhone}
                        onChange={(newValue) => setField('submittersPhone', newValue)}
                        isReadOnly={isComplete}
                        isRequired
                        errorMessage={formErrors['submittersPhone']}
                        placeholder="XXX-XXX-XXXX"
                    />
                    {isInternal &&
                        <Input
                            label="SR Number:"
                            value={formData.srNumber}
                            onChange={(newValue) => setField('srNumber', newValue)}
                            isReadOnly={isComplete}
                            errorMessage={formErrors['srNumber']}
                        />
                    }
                </div>

                <br />
                
                {/* Document references section */}
                <div className="border">
                    <Input
                        label="Location of Challenge:"
                        value={formData.challengeLocation}
                        onChange={(newValue) => setField('challengeLocation', newValue)}
                        isReadOnly={isComplete}
                        helperTextComponent={<HelperText content={"Where is the issue located at the site?"} alignment={guidanceAlignment}/>}
                        isRequired
                        errorMessage={formErrors['challengeLocation']}
                    />
                    <Input
                        label="Drawing Set Page(s):"
                        value={formData.pages}
                        onChange={(newValue) => setField('pages', newValue)}
                        isReadOnly={isComplete}
                        helperTextComponent={<HelperText content={"Drawing set number, revision date, and then page #."} alignment={guidanceAlignment}/>}
                        errorMessage={formErrors['pages']}
                    />
                    <Input
                        label="Other References:"
                        value={formData.references}
                        onChange={(newValue) => setField('references', newValue)}
                        isReadOnly={isComplete}
                        helperTextComponent={<HelperText content={"Pictures? Drawings?"} alignment={guidanceAlignment}/>}
                        errorMessage={formErrors['references']}
                    />
                </div>

                <br />

                {/* Problem description section */}
                <div className="border">
                    {/* TODO: make this a reusable component */}
                    <div className={formErrors.informationRequested ? 'row required' : 'row'}>
                        <div style={{width: '100%'}} className={isComplete ? "readonly" : ""}>
                            <div style={{display: 'flex'}}>
                                <label>
                                    Problem/Information Requested:
                                    <span className="red-asterisk"> *</span>
                                </label>
                                <HelperText 
                                    content={"Describe the problem or issue in as much detail as possible."} 
                                    alignment={guidanceAlignment}
                                />
                            </div>
                            <textarea type="text" value={formData.informationRequested} onChange={(e) => setField('informationRequested', e.target.value)} readOnly={isComplete}/>
                            {formErrors.informationRequested && <div className="error-message">*{formErrors.informationRequested}</div>}
                        </div>
                    </div>
                    {!isInternal && 
                        <div style={{display: 'flex', alignItems: 'center', marginBottom: isMobileDimensions ? '0px' : '0px'}}>
                            <input 
                                type="checkbox"
                                style={{marginRight: '10px'}}
                                checked={isSameAsSubmitter}
                                onChange={sameAsSubmitterOnClick}/>
                            <div >Same as Submitter's Name</div>
                        </div>
                    }
                    <Input
                        label="Information Requested By:"
                        value={formData.requestedBy}
                        onChange={(newValue) => setField('requestedBy', newValue)}
                        isReadOnly={isComplete}
                        helperTextComponent={<HelperText content={"Name, phone number, or email of person requesting information"} alignment={guidanceAlignment}/>}
                        isRequired
                        errorMessage={formErrors['requestedBy']}
                    />
                    <Input
                        label="Date Reply Needed:"
                        isReadOnly={isComplete}
                        isRequired
                        errorMessage={formErrors['replyNeededDate']}
                        inputComponent={
                            <DatePicker
                                selected={formData.replyNeededDate}
                                onChange={(e) => setField('replyNeededDate', e)}
                                className="date-picker"
                                readOnly={isComplete}
                            />
                        }
                    />
                </div>
                
                <br />

                {/* Interpretation section */}
                <div className="border">
                    <div className="row">
                        <div style={{width: '100%'}} className={isComplete ? "readonly" : ""}>
                            <div style={{display: 'flex'}}>
                                <label>Contractor's Interpretation and Proposed Resolution:</label>
                                <HelperText 
                                    content={"Describe the solution to the issue as clearly as possible."}
                                    alignment={guidanceAlignment}
                                />
                            </div>
                            <textarea type="text" value={formData.proposedResolution} onChange={(e) => setField('proposedResolution', e.target.value)} readOnly={isComplete}/>
                        </div>
                    </div>
                    <div className={formErrors['scheduleImpact'] ? "row required" : "row"}>
                        <div className="col-25">
                            <Checkbox 
                                label="Potential Schedule Impact:"
                                isChecked={formData.scheduleImpact}
                                onChange={(e) => setField('scheduleImpact', !formData.scheduleImpact)}
                                isReadOnly={isComplete}
                            />
                        </div>
                        {!!formData.scheduleImpact &&
                        <div className={isComplete ? "readonly col-75" : "col-75"}>
                            <input 
                                type="text" 
                                value={formData.scheduleDescription} 
                                onChange={(e) => setField('scheduleDescription', e.target.value)} 
                                placeholder="Describe impact..." 
                                readOnly={isComplete} />
                            {formErrors['scheduleImpact'] &&
                                <div className="error-message">*{formErrors['scheduleImpact']}</div>
                            }
                            {formErrors['scheduleDescription'] &&
                                <div className="error-message">*{formErrors['scheduleDescription']}</div>
                            }
                        </div>
                        }
                    </div>
                    <div className={formErrors['costImpact'] ? "row required" : "row"}>
                        <div className="col-25">
                            <Checkbox 
                                label="Potential Cost Impact:"
                                isChecked={formData.costImpact}
                                onChange={(e) => setField('costImpact', !formData.costImpact)}
                                isReadOnly={isComplete}
                            />
                        </div>
                        {!!formData.costImpact &&
                        <div className={isComplete ? "readonly col-75" : "col-75"}>
                            <input 
                                type="text" 
                                value={formData.costDescription} 
                                onChange={(e) => setField('costDescription', e.target.value)} 
                                placeholder="Describe impact..." 
                                readOnly={isComplete} />
                            {formErrors['costImpact'] &&
                                <div className="error-message">*{formErrors['costImpact']}</div>
                            }
                            {formErrors['costDescription'] &&
                                <div className="error-message">*{formErrors['costDescription']}</div>
                            }
                        </div>
                        }
                    </div>
                </div>

                <br />

                {/* PNNL Only section */}
                {isInternal && 
                    <>
                        <div className="border">
                            <div className={formErrors.response ? 'row required' : 'row'}>
                                <div style={{width: '100%'}} className={isComplete ? "readonly" : ""}>
                                    <label>
                                        Response: (PNNL ONLY)
                                        <span className="red-asterisk"> *</span>
                                    </label>
                                    <textarea type="text" value={formData.response} onChange={(e) => setField('response', e.target.value)} readOnly={isComplete}/>
                                    {formErrors.response && <div className="error-message">*{formErrors.response}</div>}
                                </div>
                            </div>
                            <div className="cols-3">
                                <Checkbox 
                                    label="Clarification Only:"
                                    isChecked={formData.isClarificationOnly}
                                    onChange={(e) => setField('isClarificationOnly', !formData.isClarificationOnly)}
                                    isReadOnly={isComplete}
                                />
                                <Checkbox 
                                    label="CN Required:"
                                    isChecked={formData.isCnRequired}
                                    onChange={(e) => setField('isCnRequired', !formData.isCnRequired)}
                                    isReadOnly={isComplete}
                                />
                                <Checkbox 
                                    label="Attached:"
                                    isChecked={formData.isAttached}
                                    onChange={(e) => setField('isAttached', !formData.isAttached)}
                                    isReadOnly={isComplete}
                                />
                            </div>
                        </div>
                        <br />
                    </>
                }

                {/* PNNL Only Section */}
                {isInternal && 
                    <>
                        <div className="border">
                            <Input
                                label="Response By:"
                                value={formData.responseBy}
                                onChange={(newValue) => setField('responseBy', newValue)}
                                isReadOnly={isComplete}
                                isRequired
                                errorMessage={formErrors['responseBy']}
                            />
                            <Input
                                label="Date:"
                                isReadOnly={isComplete}
                                isRequired
                                errorMessage={formErrors['responseDate']}
                                inputComponent={
                                    <DatePicker
                                        selected={formData.responseDate}
                                        onChange={(e) => setField('responseDate', e)}
                                        className="date-picker"
                                        readOnly={isComplete}
                                    />
                                }
                            />
                            <Input
                                label="Title:"
                                value={formData.jobTitle}
                                onChange={(newValue) => setField('jobTitle', newValue)}
                                isReadOnly={isComplete}
                                isRequired
                                errorMessage={formErrors['jobTitle']}
                            />
                        </div>
                        <br />
                    </>
                }

                {isInternal && !(formData.status === SUBMISSION_STATUS.SUBMITTED || formData.status === SUBMISSION_STATUS.IN_PROGRESS)&&
                    <>
                        <div className="border">
                            <RadioButton 
                                onChange={(newValue) => setField('classification', newValue)}
                                isReadOnly={isComplete}
                                isUnclassified={formData.classification === CLASSIFICATION.UNCLASSIFIED}
                            />
                        </div>
                        <br />
                    </>
                }

                {/* File Uploader */}
                {isEditable &&
                    <>
                        <FilePond
                            // onaddfile={(error, file) => handleAddFile(error, file)}
                            files={files}
                            allowMultiple={true} 
                            onupdatefiles={handleFileUpload}
                            maxFiles={10}
                            maxFileSize={MAX_ATTACHMENT_FILE_SIZE}
                            labelIdle={`
                                <div>Drag & Drop your files or 
                                    <span class="filepond--label-action"> 
                                        Browse
                                    </span>
                                </div> 
                                <div class="max-size">
                                    Accepted file types: ${Object.values(ACCEPTED_FILE_TYPES).map((type) => ` ${type}`)}
                                </div>
                                <div class="max-size">
                                    (Max size per file: ${MAX_ATTACHMENT_FILE_SIZE / 1000000}MB) 
                                </div>
                                <div class="max-size">
                                    Limit 10 files
                                </div>
                            `}
                            allowPaste={false}
                            acceptedFileTypes={Object.keys(ACCEPTED_FILE_TYPES)}
                            fileValidateTypeLabelExpectedTypesMap={ACCEPTED_FILE_TYPES}
                        />
                    </>
                }

                {/* Save Button */}
                {isInternal && isEditable &&
                    <>
                        <button 
                            onClick={e => loading ? null : handleSubmit(e, true)}
                            className="submit-button"
                            disabled={loading}
                        >
                            {saving ? (
                                <>
                                    <LoadingSpinner />
                                    <div style={{marginLeft: '20px'}}>Saving...</div>
                                </>
                                ) :
                                <div>Save Progress</div>
                            }
                        </button>
                    </>
                }

                {/* Submit button */}
                {!isExternalFormSubmitted && 
                    <ActionButton 
                        isInternal={isInternal}
                        status={formData.status}
                        isExternalFormSubmitted={isExternalFormSubmitted}
                        handleSubmit={e => handleSubmit(e, false)}
                        loading={loading}
                        errors={formErrors}
                        fileError={fileError}
                    />
                }

                {/* Download PDF option */}
                {/* Not visible on external site until form is submitted */}
                {isInternal &&
                    <DownloadPdfButton formData={formData} attachments={files} isInternal={true} />
                }

                {/* Download Attachments */}
                {isInternal && 
                    <div>
                        <div className="attachments-header">Download Attachments</div>
                        {loadingAttachments && 
                            <div style={{textAlign: 'center'}}>
                                <LoadingSpinner />
                            </div>
                        }
                        <div className="attachments">
                            {attachments.map((attachment, i) => {
                                const attachmentURL = URL.createObjectURL(attachment.file);
                                const fileName = attachment.fileName;

                                return (
                                    <button className="attachment-button" key={i} title={fileName}>
                                        <a 
                                            className="download-link"
                                            href={attachmentURL} 
                                            download={fileName}
                                            tabIndex={-1}>
                                                {fileName}
                                        </a>
                                        <FaFileDownload className="download-icon"/>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                }
            </div>
        </div>
    );
}


// initialize form object fields
function initializeForm (data) {
    return {
        contractNumber: data?.contractNumber || '',
        taskOrderNumber: data?.taskOrderNumber || '',
        projectName: data?.projectName || '',
        projectLocation: data?.projectLocation || '',
        contractor: data?.contractor || '',
        rfiNumber: data?.rfiNumber || '',
        'rfi-id': data?.['rfi-id'] || '',
        submissionDate: (data?.submissionDate && !isNaN(Date.parse(data.submissionDate)) && new Date(data.submissionDate)) || null,
        subject: data?.subject || '',
        submittersName: data?.submittersName || '',
        submittersEmail: data?.submittersEmail || '',
        submittersPhone: data?.submittersPhone || '',
        srNumber: data?.srNumber || '',
        challengeLocation: data?.challengeLocation || '',
        pages: data?.pages || '',
        references: data?.references || '',
        informationRequested: data?.informationRequested || '',
        requestedBy: data?.requestedBy || '',
        replyNeededDate: (data?.replyNeededDate && !isNaN(Date.parse(data.replyNeededDate)) && new Date(data.replyNeededDate)) || null,
        proposedResolution: data?.proposedResolution || '',
        scheduleImpact: data?.scheduleImpact || false,
        scheduleDescription: data?.scheduleDescription || '',
        costImpact: data?.costImpact || false,
        costDescription: data?.costDescription || '',
        response: data?.response || '',
        isClarificationOnly: data?.isClarificationOnly || false,
        isCnRequired: data?.isCnRequired || false,
        isAttached: data?.isAttached || false,
        responseBy: data?.responseBy || '',
        responseDate: (data?.responseDate && !isNaN(Date.parse(data.responseDate)) && new Date(data.responseDate)) || null,
        jobTitle: data?.jobTitle || '',
        status: data?.status || SUBMISSION_STATUS.NEW,
        classification: data?.classification || CLASSIFICATION.FOUO
    }
}

// ensure the required fields are filled in
function isRequiredFieldsComplete (data, isInternal, errorCallback, isSaving) {
    let isValid = true
    // required fields must be filled in when submitting, ignored when saving
    if (!isSaving) {
        const requiredFields = Object.entries(FORM_META).filter((kvp) => {
            //const key = kvp[0];
            const meta = kvp[1];
    
            if (isInternal) {
                return meta.isRequired;
            }
            return (meta.isRequired && !meta.pnnlOnly);
        }).map(kvp => kvp[1].key);
    
        // check for empty fields
        requiredFields.forEach((field, i) => {
            if (data[field] === null || data[field] === undefined || data[field] === '') {
                // update error messages
                errorCallback(field, 'Required field must be filled in.');
                isValid = false;
            }
        });
    }

    // checked impacts require description
    if (data.scheduleImpact) {
        if (data.scheduleDescription === '') {
            errorCallback('scheduleImpact', 'Requires a description if checked.');
            isValid = false;
        }
    }
    if (data.costImpact) {
        if (data.costDescription === '') {
            errorCallback('costImpact', 'Requires a description if checked.');
            isValid = false;
        }
    }

    // reply needed by date must be after submission date
    if (data.replyNeededDate < data.submissionDate) {
        errorCallback('replyNeededDate', 'Must be after submission date.');
        isValid = false;
    }

    // check the length of input fields
    Object.entries(FORM_META).forEach(kvp => {
        const key = kvp[0];
        const meta = kvp[1];
        
        if (meta.type === FORM_TYPES.INPUT) {
            if (data[key].length > 100) {
                errorCallback(key, "Max 100 characters.");
                isValid = false;
            }
        } else if (meta.type === FORM_TYPES.LONG_INPUT) {
            if (data[key].length > 300) {
                errorCallback(key, "Max 300 characters.");
                isValid = false;
            }
        } else if (meta.type === FORM_TYPES.TEXT_AREA) {
            if (data[key].length > 5000) {
                errorCallback(key, "Max 5000 characters.");
                isValid = false;
            }
        }
    })

    return isValid;
}
